import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { calendarRelativeFormats } from './dateFormats';
import resourcesConfig from '../resources';
import { get, isEmpty } from 'lodash';

export const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const formatTime = (orderTime, time) => {
  const minutesToAdd = Number.isFinite(Number(time)) ? Number(time) : 0;
  const base = moment(orderTime).add(minutesToAdd, 'm');
  if (!base.isValid()) return '';
  return base.format('h:mm A');
};

export const formatTimeTo12H = (time) => {
  if (!time) return '';
  const parsed = moment(time, ['HH:mm', 'HH:mm:ss', 'hh:mm A', 'h:mm A'], true);
  return parsed.isValid() ? parsed.format('h:mm a') : '';
};

export const formatDate = (date, format = 'DD MMM YYYY') => {
  if (!date) return '';
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format(format) : '';
};

export const getTimeInterval = (time) => Date.now() + time;

// NOTE: Provide only those keys in relativeDayFormats which need to be overridden
export const formatCalenderDate = (dateTime, relativeDayFormats = {}) => {
  const overriddenRelativeDayFormats = !!relativeDayFormats
    ? { ...calendarRelativeFormats, ...relativeDayFormats }
    : calendarRelativeFormats;

  const { sameDay, nextDay, nextWeek, lastDay, lastWeek, sameElse } = overriddenRelativeDayFormats;

  return moment(dateTime).calendar(null, {
    sameDay,
    nextDay,
    nextWeek,
    lastDay,
    lastWeek,
    sameElse,
  });
};

export const formatCalendarDateTime = (dateTime, relativeDayFormats, includeTime = true) => {
  return `${formatCalenderDate(dateTime, relativeDayFormats)}${
    includeTime ? `, ${formatTime(dateTime)}` : ''
  }`;
};

export const formatDateTime = (date) => {
  if (!date) return '';
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format('DD MMM YYYY, LT') : '';
};

export const formatMinutesToHoursAndMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`;
};

export const subtractFromCurrentDate = (years = 0, months = 0, days = 0) =>
  moment().subtract(years, 'y').subtract(months, 'M').subtract(days, 'd');

export const differenceBetween = (fromDate, toDate, unit = 'days', startOf) => {
  if (startOf) {
    return moment(fromDate).startOf(startOf).diff(moment(toDate).startOf(startOf), unit);
  } else {
    return moment(fromDate).diff(moment(toDate), unit);
  }
};

export const formatTransactionDate = (date, timeFirst = false) => {
  const inputDate = moment(date);
  if (inputDate.isValid()) {
    const now = moment();
    const tempDate = inputDate.clone().startOf('day');
    const daysAgo = now.diff(tempDate, 'days');

    if (daysAgo === 0) {
      // Today
      return inputDate.format(timeFirst ? 'h:mm a, [Today] ' : '[Today], h:mm a');
    } else if (daysAgo === 1) {
      // Yesterday
      return inputDate.format(timeFirst ? ', h:mm a, [Yesterday]' : '[Yesterday], h:mm a');
    } else if (daysAgo <= 6) {
      // Weekdays for past week
      return inputDate.format(timeFirst ? 'h:mm a, dddd' : 'dddd, h:mm a');
    } else {
      // DD MMM YYYY format
      return inputDate.format('DD MMM YYYY');
    }
  } else {
    return '';
  }
};

export const formatTransactionDateWithoutTime = (date) => {
  const inputDate = moment(date);
  if (inputDate.isValid()) {
    const now = moment();
    const tempDate = inputDate.clone().startOf('day');
    const daysAgo = now.diff(tempDate, 'days');

    if (daysAgo === 0) {
      // Today
      return 'Today';
    } else if (daysAgo === 1) {
      // Yesterday
      return 'Yesterday';
    } else if (daysAgo <= 6) {
      // Weekdays for past week
      return inputDate.format('dddd');
    } else {
      // DD MMM YYYY format
      return inputDate.format('DD MMM YYYY');
    }
  } else {
    return '';
  }
};

export const formatDateToISO = (date) => {
  return moment(date).format(ISO_DATE_FORMAT);
};

export const formatDateToUTC = (date) => {
  const d = moment(date);
  if (!d.isValid()) return '';
  return d.utc().toISOString();
};

/**
 * Return local time in 12-hour format (e.g. "8:37pm")
 */
export const formatLocalTime = (date) => {
  if (!date) return '';
  return moment(date).local().format('h:mma');
};

/**
 * Return a relative/local date label (e.g. "today", "tomorrow", "25 Feb", "25 Feb 2025")
 * - Drops the year when the date is in the current year.
 * - Returns lowercase strings to keep display consistent.
 */
export const formatLocalRelative = (date) => {
  if (!date) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  const localMoment = m.local();
  // Get calendar-relative label (Today/Tomorrow/Weekday or full date)
  let relative = formatCalenderDate(localMoment);

  // If calendar returned a full date (e.g. "25 Feb 2026"), drop the year when it's the current year.
  const fullDateWithYear = localMoment.format('DD MMM YYYY');
  if (relative === fullDateWithYear) {
    const currentYear = moment().year();
    if (localMoment.year() === currentYear) {
      relative = localMoment.format('DD MMM'); // e.g. "25 Feb"
    }
  }

  return String(relative).toLowerCase();
};

// Week starts from Monday and ends on Sunday
export const getLastWeek = () => {
  let fromDate = moment().subtract(1, 'weeks').startOf('week').add(1, 'day');
  let toDate = moment().subtract(1, 'weeks').endOf('week').add(1, 'day');

  return { fromDate, toDate };
};

export const getLastMonth = () => {
  let fromDate = moment().subtract(1, 'months').startOf('month');
  let toDate = moment().subtract(1, 'months').endOf('month');

  return { fromDate, toDate };
};

export const getStartOfMonth = () => {
  return moment().startOf('month');
};

// Week starts from Monday
export const getStartOfWeek = () => {
  return moment().startOf('week');
};

export const getTodayDate = () => {
  return moment();
};

export const isDateInvalid = (date, format = 'DD-MM-YYYY') => !moment(date, format, true).isValid();

export const renderOutletTiming = (outlet) => {
  const currentDay = moment().format('dddd').toUpperCase();
  const todayWorkHours =
    get(outlet, 'workhours', []).find((whour) => !whour.serviceType && whour.day === currentDay) ||
    {};
  if (todayWorkHours) {
    const startTimeIn12Hr = formatTimeTo12H(get(todayWorkHours, 'startTime'));
    const endTimeIn12Hr = formatTimeTo12H(get(todayWorkHours, 'endTime'));

    return `${startTimeIn12Hr} - ${endTimeIn12Hr}`;
  } else {
    return <FormattedMessage id="closed" defaultMessage={resourcesConfig.closed} />;
  }
};

export const renderCouponActiveRange = (startDate) => {
  const dateRange = formatDateTime(startDate);
  if (!isEmpty(dateRange)) {
    return (
      <li>
        <FormattedMessage
          id="validFrom"
          defaultMessage={resourcesConfig.validFrom}
          values={{ redeemableTime: dateRange }}
        />
      </li>
    );
  }
};

/**
 * @param {string} from
 * @param {string} to
 * @returns return true/false
 */
export const checkTimeInRange = (from, to, time) => {
  if (from == null || to == null) {
    return false;
  }
  if (String(from).trim() === '' || String(to).trim() === '') {
    return false;
  }

  const fromTime = moment(from, 'HH:mm');
  const toTime = moment(to, 'HH:mm');
  const currentTime = time ? moment(time, 'HH:mm') : moment();

  if (!fromTime.isValid() || !toTime.isValid() || (time && !currentTime.isValid())) {
    return false;
  }

  // Check if 'from' time is greater than 'to' time (for ranges crossing midnight)
  let isCrossingMidnight = false;
  if (fromTime.isAfter(toTime)) {
    isCrossingMidnight = true;
  }

  // Check if current time falls within the range
  if (isCrossingMidnight) {
    if (currentTime.isSameOrAfter(fromTime) || currentTime.isSameOrBefore(toTime)) {
      return true;
    }
  } else {
    if (currentTime.isSameOrAfter(fromTime) && currentTime.isSameOrBefore(toTime)) {
      return true;
    }
  }

  return false;
};
